import {
  ALargeSmall,
  CheckCircle,
  ChevronLeft,
  Edit,
  FileText,
  Folder,
  History,
  Image,
  List,
  LogOut,
  MapPin,
  Package,
  PackageMinus,
  PackagePlus,
  Plane,
  PlusCircle,
  Search,
  Send,
  Trash,
  UserCircle,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/store";
import { useSessionExpirationCheck } from "../../hooks/useSessionExpirationCheck";
import { toast } from "sonner";

import "./../../styles/Estoque.css";
import { MovimentacaoEstoque, Produto } from "../../types/types";

const Estoque = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const sessionExpiration = useSelector(
    (state: RootState) => state.auth.SessionExpiration,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useSessionExpirationCheck(sessionExpiration, token);

  const onLoad = useCallback(async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API}/api/Estoque/GetAllProdutos`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (res.ok) {
      const data = await res.json();
      setProdutos(data);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/area-do-cliente");
      return;
    }

    document.title = "Íkone | Estoque Geral";

    onLoad();

    return () => {};
  }, [dispatch, navigate, sessionExpiration, token, onLoad]);

  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtoAtual, setProdutoAtual] = useState<Produto>({
    id: 0,
    nome: "",
    quantidade: 0,
    quantidadeMinima: 0,
    quantidadeMaxima: 0,
    fornecedor: "",
    especificacoes: "",
    projeto: "",
    serveParaRaioX: "",
    imagemUrl: "",
    localizacao: "",
    ultimoUsuario: "",
    observacao: "",
  });

  const [pesquisarShown, setPesquisarShown] = useState(false);
  const [addProductoShown, setAddProductShown] = useState(false);
  const [removeProductoShown, setRemoveProductShown] = useState(false);
  const [historico, setHistorico] = useState<MovimentacaoEstoque[]>([]);
  const [historicoLoading, setHistoricoLoading] = useState(false);
  const [modalShown, setModalShown] = useState(false);
  const [modalEditShown, setModalEditShown] = useState(false);
  const [quantidade, setQuantidade] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [formData, setFormData] = useState<Produto>({
    id: 0,
    ultimoUsuario: user?.login || "",
    nome: "",
    quantidade: 0,
    quantidadeMinima: 0,
    quantidadeMaxima: 0,
    fornecedor: "",
    especificacoes: "",
    projeto: "",
    serveParaRaioX: "",
    imagemUrl: "",
    localizacao: "",
    observacao: "",
  });

  const [projeto, setProjeto] = useState<string>("");
  const [fornecedor, setFornecedor] = useState("");
  const [localizacao, setLocalizacao] = useState("");
  const [status, setStatus] = useState("");

  const projetos = Array.from(new Set(produtos.map((p) => p.projeto)));
  const localizacoes = Array.from(new Set(produtos.map((p) => p.localizacao)));
  const fornecedores = Array.from(new Set(produtos.map((p) => p.fornecedor)));

  const getStatus = (quantidade: number, min: number, max: number) => {
    if (quantidade === 0) return "Sem Estoque";

    const percentual = (quantidade / max) * 100;
    const percentualMin = (min / max) * 100;

    if (percentual <= percentualMin) return "Crítico";
    if (percentual > percentualMin && percentual <= 50) return "Pouco Estoque";
    if (percentual > 50 && percentual <= 100) return "Em Estoque";
    return "Estoque Alto";
  };

  const produtosFiltrados = produtos.filter((item) => {
    const nomeMatches = item.nome
      .toLowerCase()
      .includes(termoPesquisa.toLowerCase());
    const projetoMatches = projeto === "" || item.projeto === projeto;
    const fornecedorMatches =
      fornecedor === "" || item.fornecedor === fornecedor;
    const localizacaoMatches =
      localizacao === "" || item.localizacao === localizacao;
    const statusMatches =
      status === "" ||
      getStatus(
        item.quantidade,
        item.quantidadeMinima,
        item.quantidadeMaxima,
      ) === status;

    return (
      nomeMatches &&
      projetoMatches &&
      fornecedorMatches &&
      localizacaoMatches &&
      statusMatches
    );
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, type, value } = e.target;

    const newValue =
      type === "checkbox" && "checked" in e.target
        ? (e.target as HTMLInputElement).checked
        : value;

    if (modalEditShown) {
      setProdutoAtual((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleEdit = async (produto: Produto) => {
    setProdutoAtual(produto);
    setModalEditShown(true);
    setHistoricoLoading(true);
    setHistorico([]);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produto.id}/historico`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Falha ao buscar o histórico.");

      const historicoData: MovimentacaoEstoque[] = await res.json();
      setHistorico(historicoData);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar o histórico.");
    } finally {
      setHistoricoLoading(false);
    }
  };

  const onSubmit = async (acao: string) => {
    if (acao === "Salvar") {
      try {
        const form = new FormData();

        for (const key in formData) {
          form.append(key, (formData as any)[key]);
        }

        if (file) {
          form.append("imagem", file);
        }

        const res = await fetch(`${process.env.REACT_APP_API}/api/Estoque`, {
          method: "POST",
          body: form,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          setFormData({
            id: 0,
            ultimoUsuario: user?.login || "",
            nome: "",
            quantidade: 0,
            quantidadeMinima: 0,
            quantidadeMaxima: 0,
            fornecedor: "",
            especificacoes: "",
            projeto: "",
            serveParaRaioX: "",
            imagemUrl: "",
            localizacao: "",
            observacao: "",
          });
          setFile(null);
          setModalShown(false);
          onLoad();
        } else {
          toast.error("Erro ao cadastrar produto.");
        }
      } catch (err) {
        console.error(err);
      }
    } else if (acao === "Editar") {
      try {
        const formData = new FormData();

        formData.append("Nome", produtoAtual.nome);
        formData.append("Quantidade", String(produtoAtual.quantidade));
        formData.append(
          "QuantidadeMinima",
          String(produtoAtual.quantidadeMinima),
        );
        formData.append(
          "QuantidadeMaxima",
          String(produtoAtual.quantidadeMaxima),
        );
        formData.append("Fornecedor", produtoAtual.fornecedor);
        formData.append("Especificacoes", produtoAtual.especificacoes);
        formData.append("Projeto", produtoAtual.projeto);
        formData.append("ServeParaRaioX", produtoAtual.serveParaRaioX);
        formData.append("Localizacao", produtoAtual.localizacao);
        formData.append("UltimoUsuario", user?.login || "");
        formData.append("Observacao", produtoAtual.observacao);

        if (file) {
          formData.append("Imagem", file);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("Erro ao atualizar o produto.");
        }

        await response.json();
        toast.success("Produto atualizado com sucesso.");
        setFormData({
          id: 0,
          ultimoUsuario: user?.login || "",
          nome: "",
          quantidade: 0,
          quantidadeMinima: 0,
          quantidadeMaxima: 0,
          fornecedor: "",
          especificacoes: "",
          projeto: "",
          serveParaRaioX: "",
          imagemUrl: "",
          localizacao: "",
          observacao: "",
        });
        setFile(null);
        onLoad();
        setModalEditShown(false);
      } catch (error) {
        console.error(error);
      }
    } else if (acao === "Adicionar") {
      const dadosParaEnviar = {
        quantidade,
        ultimoUsuario: user?.login || "",
      };
      await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}/adicionar`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify(dadosParaEnviar),
        },
      )
        .then((res) => {
          if (res.ok) {
            toast.success("Produto adicionado com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao adicionar produto.");
          }
        })
        .catch((err) => console.error(err));
      setQuantidade(0);
      setAddProductShown(false);
      setModalEditShown(false);
    } else if (acao === "Retirar") {
      if (produtoAtual.quantidade - quantidade < 0) {
        toast.error("Quantidade insuficiente no estoque.");
        return;
      }
      const dadosParaEnviar = {
        quantidade,
        ultimoUsuario: user?.login || "",
      };
      await fetch(
        `${process.env.REACT_APP_API}/api/Estoque/${produtoAtual.id}/remover`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(dadosParaEnviar),
        },
      )
        .then((res) => {
          if (res.ok) {
            toast.success("Produto retirado com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao retirar produto.");
          }
        })
        .catch((err) => console.error(err));
      setQuantidade(0);
      setRemoveProductShown(false);
      setModalEditShown(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Deseja excluir o produto?")) {
      await fetch(`${process.env.REACT_APP_API}/api/Estoque/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => {
          if (res.ok) {
            toast.success("Produto excluído com sucesso.");
            onLoad();
          } else {
            toast.error("Erro ao excluir produto.");
          }
        })
        .catch((err) => console.error(err));
    }
  };

  const handleSaveFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      toast.error("Nenhum arquivo selecionado.");
      return;
    }

    setFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.classList.remove("dragover");

    const files = event.dataTransfer.files;
    if (files.length > 0 && fileInputRef.current) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(files[0]);
      fileInputRef.current.files = dataTransfer.files;
      handleSaveFile({
        target: fileInputRef.current,
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.classList.add("dragover");
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.currentTarget.classList.remove("dragover");
  };

  const renderItemRow = (item: Produto) => {
    const status = getStatus(
      item.quantidade,
      item.quantidadeMinima,
      item.quantidadeMaxima,
    );

    const badgeColor = {
      "Sem Estoque": "bg-dark",
      Crítico: "bg-danger",
      "Pouco Estoque": "bg-warning",
      "Em Estoque": "bg-success",
      "Estoque Alto": "bg-primary",
    }[status];

    return (
      <tr key={item.id}>
        <td>{item.ultimoUsuario}</td>
        <td className="truncate-single-line" title={item.nome}>
          {item.nome}
        </td>
        <td className="truncate-single-line">{item.quantidade}</td>
        <td className="truncate-single-line" title={item.fornecedor}>
          {item.fornecedor}
        </td>
        <td className="truncate-single-line" title={item.especificacoes}>
          {item.especificacoes}
        </td>
        <td>
          <span className={`badge ${badgeColor}`}>{status}</span>
        </td>
        <td className="truncate-single-line" title={item.projeto}>
          {item.projeto}
        </td>
        <td>{item.serveParaRaioX}</td>
        <td className="truncate-single-line">
          {item.imagemUrl ? (
            <img
              src={process.env.REACT_APP_API + item.imagemUrl}
              alt="Imagem do item"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
              }}
            />
          ) : (
            "Sem imagem"
          )}
        </td>
        <td className="truncate-single-line" title={item.localizacao}>
          {item.localizacao}
        </td>
        <td className="text-truncate">
          <div className="d-flex gap-2">
            <Edit
              style={{ cursor: "pointer" }}
              color="blue"
              onClick={() => handleEdit(item)}
              size={20}
            />
            <Trash
              style={{ cursor: "pointer" }}
              color="red"
              onClick={() => handleDelete(item.id)}
              size={20}
            />
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="m-auto pt-4" style={{ maxWidth: "85%" }}>
      <div>
        <div className="d-flex align-items-center gap-2">
          <ChevronLeft
            size={20}
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer" }}
          />
          <h1 className="m-0">Estoque Geral</h1>
        </div>
        <p>
          Itens necessários para a montagem de cada projeto da Íkone +
          quantidade de cada um em estoque.
        </p>
      </div>

      <div className="mt-4">
        <div
          className={`d-flex mb-2 align-items-center ${
            pesquisarShown ? "justify-content-between" : "justify-content-end"
          }`}
        >
          {pesquisarShown && (
            <input
              type="text"
              placeholder="Pesquisar"
              className="form-control me-2 w-50"
              name="pesquisa"
              onChange={(ev) => setTermoPesquisa(ev.target.value)}
              id=""
            />
          )}
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setModalShown(true);
              }}
            >
              <PlusCircle size={20} /> Novo
            </span>
            <span
              className="d-flex pe-auto align-items-center gap-2"
              onClick={() => {
                setTermoPesquisa("");
                setPesquisarShown(!pesquisarShown);
              }}
            >
              {!pesquisarShown ? (
                <>
                  <Search size={20} /> Pesquisar
                </>
              ) : (
                <>
                  <X size={20} /> Pesquisar
                </>
              )}
            </span>
          </div>
        </div>
        <div className="d-flex gap-2 mb-2">
          <select
            name="projeto"
            id="projeto"
            className="form-select"
            value={projeto}
            onChange={(ev) => setProjeto(ev.target.value)}
          >
            <option value="">Todos</option>
            {projetos.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            id="status"
            title={""}
            className="form-select"
            value={status}
            onChange={(ev) => setStatus(ev.target.value)}
          >
            <option value="">Estoque</option>
            <option value="Sem Estoque">Sem Estoque</option>
            <option value="Crítico">Crítico</option>
            <option value="Pouco Estoque">Pouco Estoque</option>
            <option value="Em Estoque">Em Estoque</option>
          </select>
          <select
            id="fornecedor"
            className="form-select"
            value={fornecedor}
            onChange={(ev) => setFornecedor(ev.target.value)}
          >
            <option value="">Todos os Fornecedores</option>
            {fornecedores.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          <select
            id="localizacao"
            className="form-select"
            value={localizacao}
            onChange={(ev) => setLocalizacao(ev.target.value)}
          >
            <option value="">Todas as Localizações</option>
            {localizacoes.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="table-responsive">
          <table style={{ marginBottom: "6rem" }} className="table rounded table-hover table-bordered align-middle">
            <thead className="text-center">
              <tr>
                <th className="text-truncate">
                  <UserCircle size={20} /> Última Edição Por
                </th>
                <th className="text-truncate">
                  <ALargeSmall size={20} /> Nome do Item
                </th>
                <th className="text-truncate">
                  <Package size={20} /> Quantidade
                </th>
                <th className="text-truncate">
                  <Plane size={20} /> Fornecedor
                </th>
                <th className="text-truncate">
                  <FileText size={20} /> Especificações
                </th>
                <th className="text-truncate">
                  <CheckCircle size={20} /> Status
                </th>
                <th className="text-truncate">
                  <Folder size={20} /> Projeto
                </th>
                <th className="text-truncate">
                  <List size={20} /> Serve para o Raio-X
                </th>
                <th className="text-truncate">
                  <Image size={20} /> Imagem do Item
                </th>
                <th className="text-truncate">
                  <MapPin size={20} /> Localização
                </th>
              </tr>
            </thead>
            <tbody>
              {(pesquisarShown ? produtosFiltrados : produtos).length > 0 ? (
                (pesquisarShown ? produtosFiltrados : produtos).map(
                  renderItemRow,
                )
              ) : (
                <tr>
                  <td colSpan={11} className="text-center">
                    Nenhum item encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Modal
        show={modalShown}
        onHide={() => setModalShown(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cadastrar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            style={{
              display: "block",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <label htmlFor="nome" className="mb-1">
              Nome do Item:
            </label>
            <input
              type="text"
              id="nome"
              name="nome"
              className="form-control mb-2"
              value={formData.nome}
              onChange={handleChange}
            />

            <label htmlFor="quantidade" className="mb-1">
              Quantidade:
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className="form-control mb-2"
              value={formData.quantidade}
              onChange={handleChange}
            />

            <label htmlFor="quantidadeMinima" className="mb-1">
              Quantidade Mínima:
            </label>
            <input
              type="number"
              id="quantidadeMinima"
              name="quantidadeMinima"
              className="form-control mb-2"
              value={formData.quantidadeMinima}
              onChange={handleChange}
            />

            <label htmlFor="quantidadeMaxima" className="mb-1">
              Quantidade Máxima:
            </label>
            <input
              type="number"
              id="quantidadeMaxima"
              name="quantidadeMaxima"
              className="form-control mb-2"
              value={formData.quantidadeMaxima}
              onChange={handleChange}
            />

            <label htmlFor="fornecedor" className="mb-1">
              Fornecedor:
            </label>
            <input
              type="text"
              id="fornecedor"
              name="fornecedor"
              className="form-control mb-2"
              value={formData.fornecedor}
              onChange={handleChange}
            />

            <label htmlFor="Especificacoes" className="mb-1">
              Especificações:
            </label>
            <textarea
              id="especificacoes"
              className="form-control mb-2"
              name="especificacoes"
              value={formData.especificacoes}
              onChange={handleChange}
            ></textarea>

            <label htmlFor="projeto" className="mb-1">
              Projeto:
            </label>
            <select
              id="projeto"
              name="projeto"
              className="form-control mb-2"
              value={formData.projeto}
              onChange={handleChange}
            >
              <option value="">Selecione o projeto</option>
              <option value="MONITORA">MONITORA</option>
              <option value="CANAL DE INSPEÇÃO">CANAL DE INSPEÇÃO</option>
              <option value="INSPECIONA">INSPECIONA</option>
              <option value="TODOS">TODOS</option>
            </select>

            <label className="form-check-label" htmlFor="serveParaRaioX">
              Serve para Raio-X
            </label>

            <select
              id="serveParaRaioX"
              name="serveParaRaioX"
              className="form-control mb-2"
              value={formData.serveParaRaioX}
              onChange={handleChange}
            >
              <option value="">Selecione uma opção</option>
              <option value="TODOS">TODOS</option>
              <option value="SMITHS">SMITHS</option>
              <option value="RAPISCAN">RAPISCAN</option>
              <option value="VMI USB">VMI USB</option>
              <option value="EM ABERTO">EM ABERTO</option>
              <option value="NUCTECH 1.3">NUCTECH 1.3</option>
              <option value="NUCTECH 1.2">NUCTECH 1.2</option>
            </select>

            <div className="mb-4">
              <label
                htmlFor="fileInput"
                className="mb-1 fw-semibold text-secondary"
              >
                Imagem do Item:
              </label>

              {file ? (
                <p className="fw-semibold text-primary">{file.name}</p>
              ) : (
                <>
                  <div
                    className="upload-box border border-dashed rounded p-4 text-center"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <div
                      className="upload-icon mb-2"
                      style={{ fontSize: 48, color: "#0d6efd" }}
                    >
                      <Image size={48} />
                    </div>
                    <h4 className="mb-1">Arraste e Solte</h4>
                    <p className="mb-3 text-muted">
                      Escolha somente JPG, JPEG ou PNG
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Escolher Imagem
                    </button>
                  </div>

                  <input
                    type="file"
                    id="fileInput"
                    name="imagemUrl"
                    accept=".pdf,.doc,.docx"
                    onChange={handleSaveFile}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  />
                </>
              )}
            </div>

            <label htmlFor="localizacao" className="mb-1">
              Localização:
            </label>
            <input
              type="text"
              id="localizacao"
              name="localizacao"
              className="form-control mb-2"
              value={formData.localizacao}
              onChange={handleChange}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setModalShown(false)}
          >
            Fechar
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => onSubmit("Salvar")}
          >
            <Send size={18} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={modalEditShown}
        onHide={() => setModalEditShown(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Editar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            style={{
              display: "block",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <label htmlFor="Nome" className="mb-1">
              Nome do Item:
            </label>
            <input
              type="text"
              id="nome"
              name="nome"
              className="form-control mb-2"
              value={produtoAtual.nome}
              onChange={handleChange}
            />

            <label htmlFor="quantidade" className="mb-1">
              Quantidade:
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className="form-control mb-2"
              value={produtoAtual.quantidade}
              onChange={handleChange}
            />

            <label htmlFor="quantidadeMinima" className="mb-1">
              Quantidade Mínima:
            </label>
            <input
              type="number"
              id="quantidadeMinima"
              name="quantidadeMinima"
              className="form-control mb-2"
              value={produtoAtual.quantidadeMinima}
              onChange={handleChange}
            />

            <label htmlFor="quantidadeMaxima" className="mb-1">
              Quantidade Máxima:
            </label>
            <input
              type="number"
              id="quantidadeMaxima"
              name="quantidadeMaxima"
              className="form-control mb-2"
              value={produtoAtual.quantidadeMaxima}
              onChange={handleChange}
            />
            <label htmlFor="fornecedor" className="mb-1">
              Fornecedor:
            </label>
            <input
              type="text"
              id="fornecedor"
              name="fornecedor"
              className="form-control mb-2"
              value={produtoAtual.fornecedor}
              onChange={handleChange}
            />

            <label htmlFor="especificacoes" className="mb-1">
              Especificações:
            </label>
            <textarea
              id="especificacoes"
              className="form-control mb-2"
              name="especificacoes"
              value={produtoAtual.especificacoes}
              onChange={handleChange}
            ></textarea>

            <label htmlFor="projeto" className="mb-1">
              Projeto:
            </label>
            <select
              id="projeto"
              name="projeto"
              className="form-control mb-2"
              value={produtoAtual.projeto}
              onChange={handleChange}
            >
              <option value="">Selecione o projeto</option>
              <option value="MONITORA">MONITORA</option>
              <option value="CANAL DE INSPEÇÃO">CANAL DE INSPEÇÃO</option>
              <option value="INSPECIONA">INSPECIONA</option>
              <option value="TODOS">TODOS</option>
            </select>

            <label className="form-check-label" htmlFor="serveParaRaioX">
              Serve para Raio-X
            </label>

            <select
              id="serveParaRaioX"
              name="serveParaRaioX"
              className="form-control mb-2"
              value={produtoAtual.serveParaRaioX}
              onChange={handleChange}
            >
              <option value="">Selecione uma opção</option>
              <option value="TODOS">TODOS</option>
              <option value="SMITHS">SMITHS</option>
              <option value="RAPISCAN">RAPISCAN</option>
              <option value="VMI USB">VMI USB</option>
              <option value="EM ABERTO">EM ABERTO</option>
              <option value="NUCTECH 1.3">NUCTECH 1.3</option>
              <option value="NUCTECH 1.2">NUCTECH 1.2</option>
            </select>

            <div className="mb-4">
              <label
                htmlFor="fileInput"
                className="mb-1 fw-semibold text-secondary"
              >
                Imagem do Item:
              </label>

              {file ? (
                <p className="fw-semibold text-primary">{file.name}</p>
              ) : (
                <>
                  <div
                    className="upload-box border border-dashed rounded p-4 text-center"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <div
                      className="upload-icon mb-2"
                      style={{ fontSize: 48, color: "#0d6efd" }}
                    >
                      <Image size={48} />
                    </div>
                    <h4 className="mb-1">Arraste e Solte</h4>
                    <p className="mb-3 text-muted">
                      Escolha somente JPG, JPEG ou PNG
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Escolher Imagem
                    </button>
                  </div>

                  <input
                    type="file"
                    id="fileInput"
                    name="imagemUrl"
                    accept=".pdf,.doc,.docx"
                    onChange={handleSaveFile}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  />
                </>
              )}
            </div>

            <label htmlFor="Localizacao" className="mb-1">
              Localização:
            </label>
            <input
              type="text"
              id="localizacao"
              name="localizacao"
              className="form-control mb-2"
              value={produtoAtual.localizacao}
              onChange={handleChange}
            />

            <div className="mb-3">
              <label className="form-label fw-semibold">
                <History size={16} className="me-1" />
                Histórico de Movimentações
              </label>
              <div
                className="form-control"
                style={{
                  height: "150px",
                  overflowY: "scroll",
                  backgroundColor: "#e9ecef",
                  whiteSpace: "pre-wrap",
                  fontSize: "0.85rem",
                }}
              >
                {historicoLoading
                  ? "Carregando histórico..."
                  : historico.length > 0
                    ? historico.map((mov) => (
                        <p key={mov.id} className="mb-1">
                          {`[${new Date(
                            mov.dataHoraMovimentacao,
                          ).toLocaleString(
                            "pt-BR",
                          )}] ${mov.tipoMovimentacao} de ${mov.quantidade} por ${
                            mov.usuarioResponsavel
                          }. (Estoque: ${mov.quantidadeAnterior} -> ${
                            mov.quantidadePosterior
                          })`}
                        </p>
                      ))
                    : "Nenhuma movimentação encontrada."}
              </div>
            </div>

            <div className="mb-2">
              <label htmlFor="observacao" className="form-label fw-semibold">
                Observações Adicionais
              </label>
              <textarea
                id="observacao"
                name="observacao"
                className="form-control"
                style={{ resize: "vertical", height: "100px" }}
                value={produtoAtual.observacao}
                onChange={handleChange}
              ></textarea>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setProdutoAtual({
                id: 0,
                nome: "",
                quantidade: 0,
                quantidadeMinima: 0,
                quantidadeMaxima: 0,
                fornecedor: "",
                especificacoes: "",
                projeto: "",
                serveParaRaioX: "",
                imagemUrl: "",
                localizacao: "",
                ultimoUsuario: "",
                observacao: "",
              });
              setModalEditShown(false);
            }}
          >
            Fechar
          </button>
          <button
            className="btn btn-danger d-flex align-items-center gap-2"
            onClick={() => {
              setModalEditShown(false);
              setRemoveProductShown(true);
            }}
          >
            <PackageMinus size={18} />
            Retirar
          </button>
          <button
            className="btn btn-success d-flex align-items-center gap-2"
            onClick={() => {
              setModalEditShown(false);
              setAddProductShown(true);
            }}
          >
            <PackagePlus size={18} />
            Adicionar
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => onSubmit("Editar")}
          >
            <Send size={18} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={addProductoShown}
        onHide={() => {
          setQuantidade(0);
          setModalEditShown(true);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          onClick={() => {
            setQuantidade(0);
            setModalEditShown(true);
          }}
        >
          <Modal.Title>Adicionar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <label htmlFor="Quantidade" className="mb-1">
              Quantidade:
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className="form-control mb-2"
              value={quantidade}
              onChange={(e) => setQuantidade(Number(e.target.value))}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setModalEditShown(true)}
          >
            Fechar
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => onSubmit("Adicionar")}
          >
            <Send size={18} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={removeProductoShown}
        onHide={() => {
          setQuantidade(0);
          setModalEditShown(true);
        }}
        centered
        size="lg"
      >
        <Modal.Header
          closeButton
          onClick={() => {
            setQuantidade(0);
            setModalEditShown(true);
          }}
        >
          <Modal.Title>Retirar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <label htmlFor="Quantidade" className="mb-1">
              Quantidade:
            </label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              className="form-control mb-2"
              value={quantidade}
              onChange={(e) => setQuantidade(Number(e.target.value))}
            />
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setQuantidade(0);
              setModalEditShown(true);
            }}
          >
            Fechar
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => onSubmit("Retirar")}
          >
            <Send size={18} />
            Enviar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Estoque;
